import { useState, useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import MapTouchGate from '../components/map/MapTouchGate';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { useAuth } from '../context/AuthContext';
import { useModuleLicense } from '../hooks/useModuleLicense';
import PageHeader from '../components/PageHeader';
import { Card, CardContent } from '../components/ui/Card';
import { CalendarDays, ChevronRight, MapPin, Pencil, Clock, Users, AlertCircle, AlertTriangle, CheckCircle, CircleDot, XCircle, CircleDashed, X, Lock, LockOpen, Trash2 } from 'lucide-react';
import { findOverlappingEntries } from '../utils/timesheetOverlap';
import payCycleDashboardService from '../services/payCycleDashboard';
import type { PayCycleDashboardData, PayCycleEmployee, PayCycleTimesheetEntry } from '../services/payCycleDashboard';
import timesheetLockService, { type PeriodLock } from '../services/timesheetLock';
import PayPeriodNavigator from '../components/timesheet/PayPeriodNavigator';
import { stepPayPeriod } from '../utils/payPeriod';
import { useOrgTimezone, formatTimeInTz, formatLocalDateInTz } from '../utils/timezone';

// Fix Leaflet default marker icons (bundler issue)
delete (L.Icon.Default.prototype as unknown as Record<string, unknown>)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const clockInIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const clockOutIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

// ─────────────────────────────────────────────────────────────────────────────
// Location Map Modal
// ─────────────────────────────────────────────────────────────────────────────
type LocationMapTarget = {
  label: string;
  clockIn: [number, number] | null;
  clockOut: [number, number] | null;
};

function LocationModal({ target, onClose }: { target: LocationMapTarget; onClose: () => void }) {
  const center: [number, number] = target.clockIn ?? target.clockOut ?? [-33.8688, 151.2093];
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 pt-safe overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden my-auto">
        <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200 sticky top-0 bg-white z-10">
          <h3 className="font-semibold text-neutral-900 text-sm">
            Clock locations — {target.label}
          </h3>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 p-1">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="h-64 sm:h-72">
          <MapContainer
            center={center}
            zoom={15}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={false}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            />
            {target.clockIn && (
              <Marker position={target.clockIn} icon={clockInIcon}>
                <Popup>Clock In</Popup>
              </Marker>
            )}
            {target.clockOut && (
              <Marker position={target.clockOut} icon={clockOutIcon}>
                <Popup>Clock Out</Popup>
              </Marker>
            )}
            <MapTouchGate />
          </MapContainer>
        </div>
        <div className="flex gap-4 px-4 py-2 border-t border-neutral-100 text-xs text-neutral-500">
          {target.clockIn && (
            <span className="flex items-center gap-1">
              <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
              Clock In
            </span>
          )}
          {target.clockOut && (
            <span className="flex items-center gap-1">
              <span className="inline-block w-2 h-2 rounded-full bg-red-500" />
              Clock Out
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

const CATEGORY_BADGES: Record<string, string> = {
  'Base Hourly': 'bg-neutral-100 text-neutral-700',
  'Overtime (1.5x)': 'bg-amber-100 text-amber-700',
  'Overtime (2.0x)': 'bg-orange-100 text-orange-700',
  'Time in Lieu': 'bg-purple-100 text-purple-700',
  'Personal Leave': 'bg-blue-100 text-blue-700',
  'Holiday Pay': 'bg-green-100 text-green-700',
  'Public Holiday': 'bg-emerald-100 text-emerald-700',
  'Public Holiday Worked': 'bg-teal-100 text-teal-700',
  'Time in Lieu - Taken': 'bg-violet-100 text-violet-700',
  'Long Service Leave': 'bg-indigo-100 text-indigo-700',
  'Other Leave': 'bg-sky-100 text-sky-700',
  'Meal allowance': 'bg-rose-100 text-rose-700',
};

const PAYROLL_CATEGORIES = [
  'Base Hourly', 'Overtime (1.5x)', 'Overtime (2.0x)', 'Time in Lieu',
  'Personal Leave', 'Holiday Pay', 'Public Holiday', 'Public Holiday Worked',
  'Time in Lieu - Taken', 'Long Service Leave', 'Other Leave', 'Meal allowance',
];

const APPROVAL_STYLES: Record<string, { row: string; dateRow: string; label: string; icon: typeof CheckCircle }> = {
  approved:    { row: 'bg-green-50/50',  dateRow: 'bg-green-100/70 border-l-2 border-green-500',  label: 'Approved',    icon: CheckCircle },
  pending:     { row: 'bg-amber-50/50',  dateRow: 'bg-amber-100/70 border-l-2 border-amber-500',  label: 'Pending',     icon: CircleDot },
  rejected:    { row: 'bg-red-50/50',    dateRow: 'bg-red-100/70 border-l-2 border-red-500',      label: 'Rejected',    icon: XCircle },
  unsubmitted: { row: '',                dateRow: 'bg-neutral-100/60 border-l-2 border-neutral-300', label: 'Not Submitted', icon: CircleDashed },
};

const DAY_NAMES = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function getDayName(dateStr: string): string {
  const d = new Date(dateStr + 'T12:00:00');
  const day = d.getDay(); // 0=Sun
  return DAY_NAMES[day === 0 ? 7 : day];
}

function PayCycleDashboard() {
  const { user } = useAuth();
  const orgTz = useOrgTimezone();
  const { hasAccess: hasHRAccess } = useModuleLicense('timesheet', 'hr');

  const [payFrequency, setPayFrequency] = useState<'weekly' | 'fortnightly'>('weekly');
  const [currentPeriod, setCurrentPeriod] = useState<{ startDate: string; endDate: string } | null>(null);
  const [data, setData] = useState<PayCycleDashboardData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedEmployees, setExpandedEmployees] = useState<Set<number>>(new Set());
  const [editingEntry, setEditingEntry] = useState<PayCycleTimesheetEntry | null>(null);
  const [editSaving, setEditSaving] = useState(false);
  const [deletingEntry, setDeletingEntry] = useState<PayCycleTimesheetEntry | null>(null);
  const [deleteSaving, setDeleteSaving] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [locks, setLocks] = useState<PeriodLock[]>([]);
  const [lockBusyUserId, setLockBusyUserId] = useState<number | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await payCycleDashboardService.getDashboardData({
        payFrequency,
        startDate: currentPeriod?.startDate,
        endDate: currentPeriod?.endDate,
      });
      if (response.success && response.data) {
        setData(response.data);
        // Cache the period from server response for navigation
        if (!currentPeriod) {
          setCurrentPeriod({ startDate: response.data.period.startDate, endDate: response.data.period.endDate });
        }
        // Load active locks for the resolved period (drives Lock/Reopen badges).
        const { startDate, endDate } = response.data.period;
        const lockRes = await timesheetLockService.getLocks(startDate, endDate);
        setLocks(lockRes.success && lockRes.data ? lockRes.data : []);
      } else {
        setError('Failed to load pay cycle data');
      }
    } catch (err: any) {
      setError(err?.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [payFrequency, currentPeriod]);

  // The active lock (if any) covering the current period for an employee.
  const getLockForUser = useCallback((userId: number): PeriodLock | undefined => {
    if (!currentPeriod) return undefined;
    return locks.find(l =>
      l.user_id === userId &&
      l.period_start <= currentPeriod.endDate &&
      l.period_end >= currentPeriod.startDate
    );
  }, [locks, currentPeriod]);

  const handleLock = async (emp: PayCycleEmployee) => {
    if (!currentPeriod) return;
    setLockBusyUserId(emp.userId);
    setError(null);
    try {
      const res = await timesheetLockService.lockPeriod({
        user_id: emp.userId,
        period_start: currentPeriod.startDate,
        period_end: currentPeriod.endDate,
        reason: 'manual',
      });
      if (res.success && res.data) {
        setLocks(prev => [...prev.filter(l => l.id !== res.data!.id), res.data!]);
      } else {
        setError('Failed to lock pay period');
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to lock pay period');
    } finally {
      setLockBusyUserId(null);
    }
  };

  const handleUnlock = async (lock: PeriodLock) => {
    setLockBusyUserId(lock.user_id);
    setError(null);
    try {
      const res = await timesheetLockService.unlockPeriod(lock.id);
      if (res.success) {
        setLocks(prev => prev.filter(l => l.id !== lock.id));
      } else {
        setError('Failed to reopen pay period');
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to reopen pay period');
    } finally {
      setLockBusyUserId(null);
    }
  };

  useEffect(() => {
    if (!user?.organisation_id || !hasHRAccess) return;
    loadData();
  }, [user?.organisation_id, loadData, hasHRAccess]);

  const handleFrequencyChange = (freq: 'weekly' | 'fortnightly') => {
    if (freq === payFrequency) return;
    setPayFrequency(freq);
    setCurrentPeriod(null); // reset so server calculates current cycle
    setExpandedEmployees(new Set());
  };

  const navigatePeriod = (direction: 'prev' | 'next') => {
    if (!currentPeriod) return;
    setCurrentPeriod(stepPayPeriod(currentPeriod, payFrequency, direction));
    setExpandedEmployees(new Set());
  };

  const toggleEmployee = (userId: number) => {
    setExpandedEmployees(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const handleEditSave = async (data: { payroll_category: string; total_hours: number; notes: string | null }) => {
    if (!editingEntry) return;
    setEditSaving(true);
    try {
      const response = await payCycleDashboardService.updateEntry(editingEntry.id, data);
      if (response.success) {
        setEditingEntry(null);
        loadData();
      } else {
        setError('Failed to update entry');
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to update entry');
    } finally {
      setEditSaving(false);
    }
  };

  const openDelete = (entry: PayCycleTimesheetEntry) => {
    setDeleteError(null);
    setDeletingEntry(entry);
  };

  const handleDeleteConfirm = async (reason: string) => {
    if (!deletingEntry) return;
    setDeleteSaving(true);
    setDeleteError(null);
    try {
      const response = await payCycleDashboardService.deleteEntry(deletingEntry.id, reason);
      if (response.success) {
        setDeletingEntry(null);
        loadData();
      } else {
        setDeleteError('Failed to delete entry');
      }
    } catch (err: any) {
      setDeleteError(err?.response?.data?.error?.message || err?.message || 'Failed to delete entry');
    } finally {
      setDeleteSaving(false);
    }
  };

  if (!hasHRAccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <CalendarDays className="h-12 w-12 text-neutral-300 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-neutral-700">Access Restricted</h2>
          <p className="text-sm text-neutral-500 mt-1">You need the HR role to access this dashboard.</p>
        </div>
      </div>
    );
  }

  // Group entries by date for expanded view
  const groupEntriesByDate = (entries: PayCycleTimesheetEntry[]) => {
    const grouped: Map<string, PayCycleTimesheetEntry[]> = new Map();
    for (const entry of entries) {
      const dateEntries = grouped.get(entry.date) || [];
      dateEntries.push(entry);
      grouped.set(entry.date, dateEntries);
    }
    return grouped;
  };

  const renderTotalsBadges = (totals: PayCycleEmployee['totals'], isFortnightly: boolean) => {
    const items: { label: string; value: number; className: string }[] = [
      { label: 'Base', value: totals.baseHours, className: CATEGORY_BADGES['Base Hourly'] },
      { label: 'OT 1.5x', value: totals.overtime15x, className: CATEGORY_BADGES['Overtime (1.5x)'] },
      { label: 'OT 2.0x', value: totals.overtime20x, className: CATEGORY_BADGES['Overtime (2.0x)'] },
    ];
    if (isFortnightly) {
      items.push({ label: 'TIL', value: totals.timeInLieu, className: CATEGORY_BADGES['Time in Lieu'] });
    }
    if (totals.personalLeave > 0) items.push({ label: 'Sick', value: totals.personalLeave, className: CATEGORY_BADGES['Personal Leave'] });
    if (totals.holidayPay > 0) items.push({ label: 'Holiday', value: totals.holidayPay, className: CATEGORY_BADGES['Holiday Pay'] });
    if (totals.publicHoliday > 0) items.push({ label: 'PH', value: totals.publicHoliday, className: CATEGORY_BADGES['Public Holiday'] });
    if (totals.publicHolidayWorked > 0) items.push({ label: 'PH Worked', value: totals.publicHolidayWorked, className: CATEGORY_BADGES['Public Holiday Worked'] });
    if (totals.timeInLieuTaken > 0) items.push({ label: 'TIL Taken', value: totals.timeInLieuTaken, className: CATEGORY_BADGES['Time in Lieu - Taken'] });
    if (totals.longServiceLeave > 0) items.push({ label: 'LSL', value: totals.longServiceLeave, className: CATEGORY_BADGES['Long Service Leave'] });
    if (totals.otherLeave > 0) items.push({ label: 'Other Leave', value: totals.otherLeave, className: CATEGORY_BADGES['Other Leave'] });

    return (
      <div className="flex flex-wrap gap-1.5">
        {items.filter(i => i.value > 0).map(item => (
          <span key={item.label} className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${item.className}`}>
            {item.label}: {item.value.toFixed(1)}h
          </span>
        ))}
      </div>
    );
  };

  const isFortnightly = payFrequency === 'fortnightly';

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Pay Cycle Dashboard"
        subtitle="View employee timesheet data by pay cycle"
        icon={<CalendarDays className="h-6 w-6 text-white" />}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Controls */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <PayPeriodNavigator
            mode={payFrequency}
            onModeChange={m => handleFrequencyChange(m as 'weekly' | 'fortnightly')}
            period={currentPeriod}
            onNavigate={navigatePeriod}
            disabled={loading}
          />
        </div>

        {/* Approval status legend */}
        <div className="flex flex-wrap items-center gap-4 mb-4 text-xs text-neutral-500">
          <span className="font-medium text-neutral-600">Approval status:</span>
          <span className="flex items-center gap-1"><CheckCircle className="h-3 w-3 text-green-600" /> Approved</span>
          <span className="flex items-center gap-1"><CircleDot className="h-3 w-3 text-amber-600" /> Pending</span>
          <span className="flex items-center gap-1"><XCircle className="h-3 w-3 text-red-600" /> Rejected</span>
          <span className="flex items-center gap-1"><CircleDashed className="h-3 w-3 text-neutral-400" /> Not Submitted</span>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center gap-2">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* Summary cards */}
        {data && !loading && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3 mb-6">
            <SummaryCard label="Employees" value={data.aggregateTotals.totalEmployees} unit="" icon={<Users className="h-4 w-4 text-neutral-500" />} />
            <SummaryCard label="Base Hours" value={data.aggregateTotals.baseHours} color="text-neutral-700" />
            <SummaryCard label="OT 1.5x" value={data.aggregateTotals.overtime15x} color="text-amber-700" />
            <SummaryCard label="OT 2.0x" value={data.aggregateTotals.overtime20x} color="text-orange-700" />
            {isFortnightly && (
              <SummaryCard label="TIL" value={data.aggregateTotals.timeInLieu} color="text-purple-700" />
            )}
            <SummaryCard label="Personal Leave" value={data.aggregateTotals.personalLeave} color="text-blue-700" />
            <SummaryCard label="Total Hours" value={data.aggregateTotals.totalHours} color="text-primary-700" />
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        )}

        {/* Employee table */}
        {data && !loading && (
          <Card>
            <CardContent className="p-0">
              {data.employees.length === 0 ? (
                <div className="py-12 text-center text-neutral-500">
                  No {payFrequency} employees found for this period.
                </div>
              ) : (
                <div className="divide-y divide-neutral-200">
                  {data.employees.map(emp => {
                    const isExpanded = expandedEmployees.has(emp.userId);
                    const dateGroups = isExpanded ? groupEntriesByDate(emp.entries) : null;
                    const lock = getLockForUser(emp.userId);
                    const lockBusy = lockBusyUserId === emp.userId;

                    return (
                      <div key={emp.userId}>
                        {/* Employee summary row */}
                        {/*
                          Stacks on a phone: a full name, the category badges,
                          the total and a Lock button do not share 360px, and
                          the Lock button was the piece pushed off the edge.
                        */}
                        <div className="w-full flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-4 py-3 hover:bg-neutral-50 transition-colors">
                          <button
                            onClick={() => toggleEmployee(emp.userId)}
                            className="flex items-center gap-3 min-w-0 flex-1 text-left"
                          >
                            <ChevronRight className={`h-4 w-4 text-neutral-400 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                            <div className="min-w-0">
                              <span className="font-medium text-neutral-900">
                                {emp.firstName} {emp.lastName}
                              </span>
                              <span className="text-xs text-neutral-400 ml-2">@{emp.username}</span>
                            </div>
                          </button>
                          <div className="flex flex-wrap items-center gap-2 sm:gap-3 sm:flex-shrink-0 pl-7 sm:pl-0">
                            {renderTotalsBadges(emp.totals, isFortnightly)}
                            <span className="font-semibold text-neutral-900 text-sm whitespace-nowrap">
                              {emp.totals.totalHours.toFixed(1)}h
                            </span>
                            {lock ? (
                              <div className="flex items-center gap-1.5">
                                <span
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-neutral-800 text-white"
                                  title={`Finalised for payroll${lock.locked_by_username ? ` by ${lock.locked_by_username}` : ''}`}
                                >
                                  <Lock className="h-3 w-3" /> Locked
                                </span>
                                <button
                                  onClick={() => handleUnlock(lock)}
                                  disabled={lockBusy}
                                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-neutral-600 border border-neutral-300 hover:bg-neutral-100 disabled:opacity-50 transition-colors"
                                  title="Reopen this pay period for edits"
                                >
                                  <LockOpen className="h-3 w-3" /> {lockBusy ? '…' : 'Reopen'}
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => handleLock(emp)}
                                disabled={lockBusy}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-neutral-600 border border-neutral-300 hover:bg-neutral-100 disabled:opacity-50 transition-colors"
                                title="Lock (finalise) this pay period so no further changes can be made"
                              >
                                <Lock className="h-3 w-3" /> {lockBusy ? '…' : 'Lock'}
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Expanded detail */}
                        {isExpanded && dateGroups && (
                          <div className="bg-neutral-50 border-t border-neutral-100">
                            {lock && (
                              <div className="flex items-center gap-2 px-4 py-2 bg-neutral-800 text-white text-xs">
                                <Lock className="h-3.5 w-3.5" />
                                Finalised for payroll on {formatLocalDateInTz(lock.locked_at.split('T')[0], orgTz)}
                                {lock.locked_by_username ? ` by ${lock.locked_by_username}` : ''}. Reopen to make changes.
                              </div>
                            )}
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="text-xs text-neutral-500 uppercase tracking-wider">
                                    <th className="text-left py-2 px-3 font-medium">Time</th>
                                    <th className="text-left py-2 px-3 font-medium">Raw Clock</th>
                                    <th className="text-left py-2 px-3 font-medium">Location</th>
                                    <th className="text-right py-2 px-3 font-medium">Hours</th>
                                    <th className="text-left py-2 px-3 font-medium">Category</th>
                                    <th className="text-left py-2 px-3 font-medium">Notes</th>
                                    <th className="text-center py-2 px-3 font-medium w-16"></th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-neutral-100">
                                  {Array.from(dateGroups.entries()).map(([date, entries]) => {
                                    const approvalStatus = entries[0]?.approvalStatus || 'unsubmitted';
                                    const style = APPROVAL_STYLES[approvalStatus] || APPROVAL_STYLES.unsubmitted;
                                    const StatusIcon = style.icon;
                                    const overlapping = findOverlappingEntries(entries);
                                    return (
                                      <>
                                        {/* Date header */}
                                        <tr key={`date-${date}`} className={style.dateRow}>
                                          <td colSpan={7} className="px-3 py-1.5 font-medium text-neutral-700 text-xs">
                                            <div className="flex items-center justify-between gap-2">
                                              <span className="flex flex-wrap items-center gap-x-2">
                                                {getDayName(date)}, {formatLocalDateInTz(date, orgTz)}
                                                {overlapping.size > 0 && (
                                                  <span className="inline-flex items-center gap-1 text-red-700">
                                                    <AlertTriangle className="h-3 w-3" />
                                                    Entries overlap — the same time is counted twice
                                                  </span>
                                                )}
                                              </span>
                                              <span className="flex items-center gap-1 text-[11px] font-normal text-neutral-500">
                                                <StatusIcon className="h-3 w-3" />
                                                {style.label}
                                              </span>
                                            </div>
                                          </td>
                                        </tr>
                                        {/* Entry rows — editing disabled while the period is locked */}
                                        {entries.map(entry => (
                                          <EntryRow
                                            key={entry.id}
                                            entry={entry}
                                            orgTz={orgTz}
                                            rowClassName={overlapping.has(entry.id) ? 'bg-red-50' : style.row}
                                            overlaps={overlapping.has(entry.id)}
                                            onEdit={lock ? undefined : setEditingEntry}
                                            onDelete={lock ? undefined : openDelete}
                                          />
                                        ))}
                                      </>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}
        {editingEntry && (
          <EditEntryModal
            entry={editingEntry}
            saving={editSaving}
            onSave={handleEditSave}
            onClose={() => setEditingEntry(null)}
          />
        )}
        {deletingEntry && (
          <DeleteEntryModal
            entry={deletingEntry}
            orgTz={orgTz}
            saving={deleteSaving}
            error={deleteError}
            onConfirm={handleDeleteConfirm}
            onClose={() => setDeletingEntry(null)}
          />
        )}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, unit = 'h', color = 'text-neutral-900', icon }: {
  label: string;
  value: number;
  unit?: string;
  color?: string;
  icon?: React.ReactNode;
}) {
  return (
    <Card>
      <CardContent className="p-3">
        <div className="flex items-center gap-1.5 mb-1">
          {icon}
          <span className="text-xs text-neutral-500 font-medium">{label}</span>
        </div>
        <span className={`text-lg font-bold ${color}`}>
          {unit ? value.toFixed(1) : value}{unit}
        </span>
      </CardContent>
    </Card>
  );
}

function EntryRow({ entry, orgTz, rowClassName = '', overlaps = false, onEdit, onDelete }: {
  entry: PayCycleTimesheetEntry;
  orgTz: string;
  rowClassName?: string;
  overlaps?: boolean;
  onEdit?: (entry: PayCycleTimesheetEntry) => void;
  onDelete?: (entry: PayCycleTimesheetEntry) => void;
}) {
  const [showMap, setShowMap] = useState(false);
  const isInProgress = entry.status === 'clocked_in';
  const hasOriginalIn = entry.originalClockInTime && entry.originalClockInTime !== entry.clockInTime;
  const hasOriginalOut = entry.originalClockOutTime && entry.clockOutTime && entry.originalClockOutTime !== entry.clockOutTime;
  const badgeClass = CATEGORY_BADGES[entry.payrollCategory] || 'bg-neutral-100 text-neutral-700';
  const hasLocation = entry.clockInLatitude != null || entry.clockOutLatitude != null;

  return (
    <tr className={`hover:bg-white/60 ${rowClassName}`}>
      {/* Adjusted times */}
      <td className="px-3 py-2 whitespace-nowrap">
        <div className="flex items-center gap-1">
          <span className="text-neutral-900">{formatTimeInTz(entry.clockInTime, orgTz)}</span>
          <span className="text-neutral-400">—</span>
          {isInProgress ? (
            <span className="inline-flex items-center gap-1 text-yellow-700 bg-yellow-100 px-1.5 py-0.5 rounded text-xs font-medium">
              <Clock className="h-3 w-3" /> In Progress
            </span>
          ) : entry.clockOutTime ? (
            <span className="text-neutral-900">{formatTimeInTz(entry.clockOutTime, orgTz)}</span>
          ) : (
            <span className="text-neutral-400">—</span>
          )}
          {entry.edited && (
            <span title={`Edited${entry.editReason ? ': ' + entry.editReason : ''}`}>
              <Pencil className="h-3 w-3 text-amber-500 ml-1" />
            </span>
          )}
        </div>
      </td>

      {/* Raw/original clock times */}
      <td className="px-3 py-2 whitespace-nowrap text-xs text-neutral-500">
        {(hasOriginalIn || hasOriginalOut) ? (
          <span>
            {hasOriginalIn ? formatTimeInTz(entry.originalClockInTime!, orgTz) : '—'}
            {' — '}
            {hasOriginalOut ? formatTimeInTz(entry.originalClockOutTime!, orgTz) : '—'}
          </span>
        ) : (
          <span className="text-neutral-300">—</span>
        )}
      </td>

      {/* Location */}
      <td className="px-3 py-2">
        {hasLocation ? (
          <button
            onClick={() => setShowMap(true)}
            className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
          >
            <MapPin className="h-3 w-3" />
            Map
          </button>
        ) : (
          <span className="text-neutral-300 text-xs">—</span>
        )}
        {showMap && (
          <LocationModal
            target={{
              label: formatTimeInTz(entry.clockInTime, orgTz),
              clockIn: entry.clockInLatitude != null && entry.clockInLongitude != null
                ? [entry.clockInLatitude, entry.clockInLongitude]
                : null,
              clockOut: entry.clockOutLatitude != null && entry.clockOutLongitude != null
                ? [entry.clockOutLatitude, entry.clockOutLongitude]
                : null,
            }}
            onClose={() => setShowMap(false)}
          />
        )}
      </td>

      {/* Hours */}
      <td className="px-3 py-2 text-right whitespace-nowrap font-medium text-neutral-900">
        {isInProgress ? '—' : entry.totalHours.toFixed(2)}
      </td>

      {/* Category */}
      <td className="px-3 py-2">
        <div className="flex flex-col gap-0.5">
          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${badgeClass}`}>
            {entry.payrollCategory}
          </span>
          {entry.holidayName && (
            <span className="text-[10px] text-emerald-700 font-medium" title={entry.holidayName}>
              {entry.holidayName}
            </span>
          )}
          {entry.skipLunchDeduction && (entry.lunchWorkedApprovedAt ? (
            <span className="text-[10px] text-sky-700 font-medium" title="Penalty rates apply to a worked meal break">
              Lunch worked — asked by {entry.lunchWorkedApprovedByName || 'supervisor'}
            </span>
          ) : (
            <span className="text-[10px] text-orange-700 font-medium" title="No supervisor recorded asking for this. Penalty rates apply to a worked meal break.">
              No lunch — not approved
            </span>
          ))}
          {entry.missedClock && (
            <span
              className="text-[10px] text-indigo-700 font-medium"
              title={entry.missedClockReason
                ? `Scheduled hours claimed by employee — no clock record: ${entry.missedClockReason}`
                : 'Scheduled hours claimed by employee — no clock record'}
            >
              No clock record
            </span>
          )}
          {overlaps && (
            <span
              className="text-[10px] text-red-700 font-medium"
              title="This entry covers time another entry on this day already covers. Correct or delete the duplicate."
            >
              Overlaps another entry
            </span>
          )}
        </div>
      </td>

      {/* Notes */}
      <td className="px-3 py-2 text-xs text-neutral-500 max-w-[150px] truncate" title={entry.notes || ''}>
        {entry.notes || ''}
      </td>

      {/* Actions */}
      <td className="px-3 py-2 text-center whitespace-nowrap">
        {onEdit && (
          <button
            onClick={() => onEdit(entry)}
            className="p-1 rounded hover:bg-neutral-200 text-neutral-400 hover:text-neutral-700 transition-colors"
            title="Edit entry"
            aria-label="Edit entry"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
        {onDelete && !isInProgress && (
          <button
            onClick={() => onDelete(entry)}
            className="p-1 rounded hover:bg-red-100 text-neutral-400 hover:text-red-700 transition-colors"
            title="Delete entry"
            aria-label="Delete entry"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </td>
    </tr>
  );
}

function EditEntryModal({ entry, saving, onSave, onClose }: {
  entry: PayCycleTimesheetEntry;
  saving: boolean;
  onSave: (data: { payroll_category: string; total_hours: number; notes: string | null }) => void;
  onClose: () => void;
}) {
  const [category, setCategory] = useState(entry.payrollCategory);
  const [hours, setHours] = useState(entry.totalHours.toString());
  const [notes, setNotes] = useState(entry.notes || '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const parsedHours = parseFloat(hours);
    if (isNaN(parsedHours) || parsedHours < 0 || parsedHours > 24) return;
    onSave({
      payroll_category: category,
      total_hours: Math.round(parsedHours * 100) / 100,
      notes: notes.trim() || null,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200">
          <h3 className="text-lg font-semibold text-neutral-900">Edit Timesheet Entry</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-neutral-100 text-neutral-400 hover:text-neutral-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Payroll Category</label>
            <select
              value={category}
              onChange={e => setCategory(e.target.value)}
              className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {PAYROLL_CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Hours</label>
            <input
              type="number"
              step="0.01"
              min="0"
              max="24"
              value={hours}
              onChange={e => setHours(e.target.value)}
              className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Notes</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={3}
              maxLength={500}
              className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-lg hover:bg-neutral-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DeleteEntryModal({ entry, orgTz, saving, error, onConfirm, onClose }: {
  entry: PayCycleTimesheetEntry;
  orgTz: string;
  saving: boolean;
  error: string | null;
  onConfirm: (reason: string) => void;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) return;
    onConfirm(reason.trim());
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200">
          <h3 className="text-lg font-semibold text-neutral-900">Delete Timesheet Entry</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-neutral-100 text-neutral-400 hover:text-neutral-600" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="rounded-lg bg-neutral-50 border border-neutral-200 px-3 py-2 text-sm text-neutral-700">
            <div className="font-medium">
              {formatLocalDateInTz(entry.date, orgTz)} · {formatTimeInTz(entry.clockInTime, orgTz)}
              {entry.clockOutTime ? ` — ${formatTimeInTz(entry.clockOutTime, orgTz)}` : ''}
            </div>
            <div className="text-xs text-neutral-500 mt-0.5">
              {entry.totalHours.toFixed(2)}h · {entry.payrollCategory}
            </div>
          </div>
          <p className="text-sm text-neutral-600">
            The entry is removed from the timesheet and from payroll. This cannot be undone here.
          </p>

          <div>
            <label htmlFor="delete-entry-reason" className="block text-sm font-medium text-neutral-700 mb-1">Reason</label>
            <textarea
              id="delete-entry-reason"
              value={reason}
              onChange={e => setReason(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder="e.g. duplicate of the Base Hourly entry"
              className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-lg hover:bg-neutral-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !reason.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
            >
              {saving ? 'Deleting...' : 'Delete entry'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default PayCycleDashboard;
